import api from './api';
import type { DispatchQueueItem, PaginatedResponse, Rental, RateQuote, LabelResponse } from '@/types';

export interface DispatchLabelResponse {
  rentalId: string;
  outbound: LabelResponse;
  returnLabel: LabelResponse | null;
  returnLabelError?: string;
}

export interface DispatchQueueParams {
  status?: string;
  page?: number;
  limit?: number;
}

export interface AssignSerialsDto {
  assignments: Array<{
    rentalItemId: string;
    serialNumbers: string[];
  }>;
}

export interface ScanResult {
  type: 'tracking' | 'serial';
  rental: Rental;
  asset?: {
    serialNumber: string;
    status: string;
  };
}

export const warehouseApi = {
  getDispatchQueue: async (params: DispatchQueueParams = {}) => {
    const response = await api.get<PaginatedResponse<DispatchQueueItem>>('/warehouse/queue', { params });
    return response.data;
  },

  scan: async (value: string) => {
    const response = await api.get<ScanResult>(`/warehouse/scan/${encodeURIComponent(value)}`);
    return response.data;
  },

  assignSerials: async (rentalId: string, dto: AssignSerialsDto) => {
    const response = await api.post(`/warehouse/rental/${rentalId}/assign-serials`, dto);
    return response.data;
  },

  /**
   * Swap one assigned serial for another, before the order ships.
   *
   * Distinct from a replacement: that is for equipment already with a patient and
   * keeps the original unit's history. This corrects a mis-pick.
   */
  changeSerial: async (
    rentalId: string,
    dto: { oldSerial: string; newSerial: string; reason?: string },
  ) => {
    const response = await api.patch(`/warehouse/rental/${rentalId}/change-serial`, dto);
    return response.data;
  },

  markShipped: async (rentalId: string, trackingNumber?: string) => {
    const response = await api.post<Rental>(`/warehouse/rental/${rentalId}/ship`, { trackingNumber });
    return response.data;
  },

  markReadyToShip: async (rentalId: string) => {
    const response = await api.post<Rental>(`/warehouse/rental/${rentalId}/ready`);
    return response.data;
  },

  getRatesForRental: async (rentalId: string) => {
    const response = await api.get<RateQuote[]>(`/warehouse/rental/${rentalId}/rates`);
    return response.data;
  },

  generateLabel: async (rentalId: string, params: { carrierCode?: string; serviceCode?: string; provider?: string } = {}) => {
    const response = await api.post<DispatchLabelResponse>(`/warehouse/rental/${rentalId}/generate-label`, params);
    return response.data;
  },
};

export default warehouseApi;
